import { getSession } from '@wix/ambassador-bookings-calendar-v1-session/http';
import { BookingsServer } from '@wix/ambassador-bookings-server/http';
import { CalendarServer } from '@wix/ambassador-calendar-server/http';
import { CouponsServer } from '@wix/ambassador-coupons-server/http';
import {
  createCheckoutFromCurrentCart as ecomCreateCheckoutFromCurrentCart,
  updateCurrentCart,
} from '@wix/ambassador-ecom-v1-cart/http';
import {
  CreateCheckoutFromCurrentCartRequest,
  UpdateCartRequest,
  UpdateCartResponse,
} from '@wix/ambassador-ecom-v1-cart/types';
import {
  createOrder,
  updateCheckout,
} from '@wix/ambassador-ecom-v1-checkout/http';
import {
  CreateOrderRequest,
  UpdateCheckoutRequest,
} from '@wix/ambassador-ecom-v1-checkout/types';
import { MembersNgApi } from '@wix/ambassador-members-ng-api/http';
import { MembershipsSpiHost } from '@wix/ambassador-memberships-spi-host/http';
import { list } from '@wix/ambassador-pricing-plans-v2-benefit/http';
import { ServicesCatalogServer } from '@wix/ambassador-services-catalog-server/http';
import { TotalsCalculator } from '@wix/ambassador-totals-calculator/http';
import {
  CheckoutApiConfig,
  createConfig,
  getBookingsLineItemsOptions,
  getCurrentCart,
} from '@wix/bookings-checkout-api';
import type {
  NavigateToCheckoutOptions,
  NavigateToPackagePickerOptions,
} from '@wix/pricing-plans-tpa-api';
import type { Experiments } from '@wix/yoshi-flow-editor';
import {
  ControllerFlowAPI,
  IHttpClient,
  ReportError,
} from '@wix/yoshi-flow-editor';
import { IFlowApiAdapter } from '../utils/flow-api-adapter/types';
import { addToCart } from './implementations/addToCart/addToCart';
import { areCouponsAvailableForService } from './implementations/areCouponsAvailableForService/areCouponsAvailableForService';
import { calculateTotalPrice } from './implementations/calculateTotalPrice/calculateTotalPrice';
import { checkoutBooking } from './implementations/checkoutBooking/checkoutBooking';
import { fetchLocaleData as fetchLocaleDataApi } from './implementations/fetchLocaleData/fetchLocaleData';
import { generalBookingCheckoutFlow } from './implementations/generalBookingCheckoutFlow/generalBookingCheckoutFlow';
import { getAppointmentAvailability } from './implementations/getAppointmentAvailability/getAppointmentAvailability';
import { getAvailability } from './implementations/getAvailability/getAvailability';
import { getCatalogData } from './implementations/getCatalogData/getCatalogData';
import { getConfirmedBookingsByServiceAndContactId } from './implementations/getConfirmedBookingByServiceAndContactId';
import { getEventById } from './implementations/getEventById';
import { getExistingBooking } from './implementations/getExistingBooking';
import { getListAddonsGroups } from './implementations/getListAddonsGroups';
import { getMemberDetails } from './implementations/getMemberDetails/getMemberDetails';
import { getOptionsAndVariantsData } from './implementations/getOptionsAndVariantsData/getOptionsAndVariantsData';
import { getPolicyStringToUse } from './implementations/getPolicyStringToUse/getPolicyStringToUse';
import { getPublicPlans } from './implementations/getPublicPlans/getPublicPlans';
import { getScheduleAvailability } from './implementations/getScheduleAvailability/getScheduleAvailability';
import { getSlots } from './implementations/getSlots/getSlots';
import { initWixFormController } from './implementations/initWixFormController/initWixFormController';
import { isMembersAreaInstalled } from './implementations/isMembersAreaInstalled';
import { isSMSAutomationsEnabled } from './implementations/isSMSAutomationsEnabled';
import { isUpsellPluginInstalled } from './implementations/isUpsellPluginInstalled/isUpsellPluginInstalled';
import { listMemberships } from './implementations/listMemberships/listMemberships';
import { mapContactDetails } from './implementations/mapContactDetails/mapContactDetails';
import { previewPrice } from './implementations/previewPrice/previewPrice';
import { queryAddons } from './implementations/queryAddons/queryAddons';
import { queryBookings } from './implementations/queryBookings';
import { FormApiContext } from './implementations/utils';
import { queryIntakeForm } from './implementations/queryIntakeForm';

export const CATALOG_SERVER_URL = '_api/services-catalog';
export const BOOKINGS_SERVER_URL = '_api/bookings';
export const CALENDAR_SERVER_URL = '_api/calendar-server';
export const MEMBERS_SERVER_API = '_api/members/v1/members';
export const COUPONS_SERVER_URL = '_api/coupons-server';
export const TOTALS_CALCULATOR = '_api/totals-calculator';
export const MEMBERSHIPS_SPI = '_api/memberships-spi-host';
export const PAYMENT_SERVICES_URL = '_api/payment-services-web';

const BOOKINGS_FES_BASE_DOMAIN = '/_api/bookings-viewer/visitor';
const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';
const REVISION_HEADER_NAME = 'x-wix-site-revision';

export class FormApi {
  private experiments: Experiments;
  private flowApi: ControllerFlowAPI;
  private wixSdkAdapter: IFlowApiAdapter;
  private reportError: ReportError;
  private httpClient: IHttpClient;
  private catalogServer: ReturnType<typeof ServicesCatalogServer>;
  private bookingsServer: ReturnType<typeof BookingsServer>;
  private calendarServer: ReturnType<typeof CalendarServer>;
  private couponsServer: ReturnType<typeof CouponsServer>;
  private membersServer: ReturnType<typeof MembersNgApi>;
  private totalsCalculatorServer: ReturnType<typeof TotalsCalculator>;
  private membershipsServer: ReturnType<typeof MembershipsSpiHost>;

  constructor({
    httpClient,
    flowApi,
    wixSdkAdapter,
    reportError,
    experiments,
  }: {
    httpClient: IHttpClient;
    flowApi: ControllerFlowAPI;
    wixSdkAdapter: IFlowApiAdapter;
    reportError: ReportError;
    experiments: Experiments;
  }) {
    this.flowApi = flowApi;
    this.experiments = experiments;
    this.httpClient = httpClient;
    this.wixSdkAdapter = wixSdkAdapter;
    this.reportError = reportError;
    const baseUrl = wixSdkAdapter.getServerBaseUrl();
    this.catalogServer = ServicesCatalogServer(
      `${baseUrl}${CATALOG_SERVER_URL}`,
    );
    this.bookingsServer = BookingsServer(`${baseUrl}${BOOKINGS_SERVER_URL}`);
    this.calendarServer = CalendarServer(`${baseUrl}${CALENDAR_SERVER_URL}`);
    this.couponsServer = CouponsServer(`${baseUrl}${COUPONS_SERVER_URL}`);
    this.membersServer = MembersNgApi(`${baseUrl}${MEMBERS_SERVER_API}`, {
      ignoredProtoHttpUrlPart: '/v1/members',
    });
    this.totalsCalculatorServer = TotalsCalculator(
      `${baseUrl}${TOTALS_CALCULATOR}`,
    );
    this.membershipsServer = MembershipsSpiHost(`${baseUrl}${MEMBERSHIPS_SPI}`);
  }

  private getFormApiContext(): FormApiContext {
    return {
      httpClient: this.httpClient,
      flowApi: this.flowApi,
      queryBookings: this.queryBookings,
      queryBookingsByServiceId: this.queryBookingsByServiceId.bind(this),
      authorization: this.getAuthorization(),
      calendarServer: this.calendarServer,
      couponsServer: this.couponsServer,
      membersServer: this.membersServer,
      totalsCalculatorServer: this.totalsCalculatorServer,
      membershipsServer: this.membershipsServer,
      bookingsServer: this.bookingsServer,
      catalogServer: this.catalogServer,
      experiments: this.experiments,
      reportError: this.reportError,
      wixSdkAdapter: this.wixSdkAdapter,
      queryIntakeForm: this.queryIntakeForm,
    };
  }

  private wrapWithContext<TArgs extends object, TResult>(
    func: (args: TArgs & FormApiContext) => TResult,
  ): (args: TArgs) => TResult {
    return ((args: TArgs) => func({ ...args, ...this.getFormApiContext() } as TArgs & FormApiContext));
  }

  async notifyOwnerNonPremiumEnrollmentAttempt() {
    return this.httpClient.post(
      `${BOOKINGS_FES_BASE_DOMAIN}/classes/nonPremium`,
      '',
      {
        headers: {
          [REVISION_HEADER_NAME]: this.wixSdkAdapter.getSiteRevision(),
          [XSRF_HEADER_NAME]: this.wixSdkAdapter.getCsrfToken(),
        },
      },
    );
  }

  async notifyOwnerNonPricingPlanEnrollmentAttempt(data: object) {
    return this.httpClient.post(
      `${BOOKINGS_FES_BASE_DOMAIN}/pricing-plans/invalidSetup`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          [REVISION_HEADER_NAME]: this.wixSdkAdapter.getSiteRevision(),
          [XSRF_HEADER_NAME]: this.wixSdkAdapter.getCsrfToken(),
        },
      },
    );
  }

  getPolicyStringToUse = this.wrapWithContext(getPolicyStringToUse);
  getAuthorization() {
    return this.wixSdkAdapter.getInstance();
  }

  getConfig(): CheckoutApiConfig {
    return createConfig({
      experiments: this.experiments,
      getAuthorization: () => this.getAuthorization(),
      getBaseUrl: () => this.wixSdkAdapter.getServerBaseUrl(),
      httpClient: this.httpClient,
    });
  }

  async getSessionById({ sessionId }: { sessionId: string }) {
    const sessionResponse = await this.httpClient.request(
      getSession({ id: sessionId }),
    );

    return sessionResponse.data.session;
  }

  async navigateToPricePlanPackagePicker(
    options: NavigateToPackagePickerOptions,
  ) {
    const pricingPlansApi = await this.wixSdkAdapter.getPricingPlansApi();
    return pricingPlansApi.navigateToPackagePicker(options);
  }

  async navigateToPricePlanCheckout(options: NavigateToCheckoutOptions) {
    const pricingPlansApi = await this.wixSdkAdapter.getPricingPlansApi();
    return pricingPlansApi.navigateToCheckout(options);
  }

  getPublicPlans = () => getPublicPlans(this.getFormApiContext());

  async getPricingPlansBenefitList() {
    let listResponse;

    try {
      listResponse = await this.httpClient.request(list({}));
    } catch (e) {
      return [];
    }

    return listResponse?.data?.benefitsWithPlanInfo || [];
  }

  getAppointmentAvailability = this.wrapWithContext(getAppointmentAvailability);

  isMembersAreaInstalled = () => isMembersAreaInstalled(this.getFormApiContext());

  isUpsellPluginInstalled = () => isUpsellPluginInstalled(this.getFormApiContext());

  getCatalogData = this.wrapWithContext(getCatalogData);

  getSlots = this.wrapWithContext(getSlots);

  getMemberDetails = this.wrapWithContext(getMemberDetails);

  getLineItemOptionsFromCart() {
    return getBookingsLineItemsOptions(this.getConfig());
  }

  getOptionsAndVariantsData = this.wrapWithContext(getOptionsAndVariantsData);

  getListAddonsGroups = this.wrapWithContext(getListAddonsGroups);

  getAvailability = this.wrapWithContext(getAvailability);

  getScheduleAvailability = this.wrapWithContext(getScheduleAvailability);

  listMemberships = this.wrapWithContext(listMemberships);

  areCouponsAvailableForService = () => areCouponsAvailableForService(this.getFormApiContext());

  async getCartLineItems() {
    const cartLineItems = await getCurrentCart(this.getConfig());
    return cartLineItems;
  }

  addToCart = this.wrapWithContext(addToCart);

  queryBookings = this.wrapWithContext(queryBookings);

  queryIntakeForm = this.wrapWithContext(queryIntakeForm);

  generalBookingCheckoutFlow = this.wrapWithContext(generalBookingCheckoutFlow);

  checkoutBooking = this.wrapWithContext(checkoutBooking);

  async createCheckoutFromCurrentCart(
    createCheckoutFromCurrentCartRequest: CreateCheckoutFromCurrentCartRequest,
  ) {
    return (
      await this.httpClient.request(
        ecomCreateCheckoutFromCurrentCart(createCheckoutFromCurrentCartRequest),
      )
    ).data;
  }

  async createOrderFromCheckout(createOrderRequest: CreateOrderRequest) {
    return this.httpClient.request(createOrder(createOrderRequest));
  }

  async updateCart({
    cartInfo,
  }: UpdateCartRequest): Promise<UpdateCartResponse> {
    return (
      await this.httpClient.request(
        updateCurrentCart({
          cartInfo,
        }),
      )
    ).data;
  }

  async updateCheckout(updateCheckoutRequest: UpdateCheckoutRequest) {
    return this.httpClient.request(updateCheckout(updateCheckoutRequest));
  }

  calculateTotalPrice = this.wrapWithContext(calculateTotalPrice);

  initWixFormController = this.wrapWithContext(initWixFormController);

  previewPrice = this.wrapWithContext(previewPrice);

  isSMSAutomationsEnabled = this.wrapWithContext(isSMSAutomationsEnabled);

  mapContactDetails = this.wrapWithContext(mapContactDetails);

  fetchLocaleData = () => fetchLocaleDataApi(this.getFormApiContext());

  queryBookingsByServiceId = this.wrapWithContext(getConfirmedBookingsByServiceAndContactId);

  getExistingBooking = this.wrapWithContext(getExistingBooking);

  getEventById = this.wrapWithContext(getEventById);

  queryAddons = this.wrapWithContext(queryAddons);
}
